import React, {createContext, useCallback, useContext, useState} from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  readonly theme: ThemeMode;
  readonly toggleTheme: () => void;
  readonly setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  readonly children: React.ReactNode;
  readonly defaultTheme?: ThemeMode;
}

/**
 * 主题包裹 div 的样式
 * 继承父容器的 flex 布局，确保不打断布局流
 */
const themeWrapperStyle: React.CSSProperties = {
  display: 'contents'  // 关键：让这个 div "透明"，不影响布局
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light'
}) => {
  const [theme, setThemeState] = useState<ThemeMode>(defaultTheme);

  const toggleTheme = useCallback((): void => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setTheme = useCallback((newTheme: ThemeMode): void => {
    setThemeState(newTheme);
  }, []);

  const value: ThemeContextValue = {
    theme,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      <div
        className={`theme-${theme}`}
        data-theme={theme}
        style={themeWrapperStyle}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * 用于不在 ThemeProvider 内部时的安全 hook
 * 返回默认浅色主题
 */
export const useThemeSafe = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: 'light',
      toggleTheme: () => {},
      setTheme: () => {}
    };
  }
  return context;
};